/**
 * Date formats — one numeric-date setting for the whole app.
 *
 * A date is shown as numbers in the chosen format (16.10.2026, 10/16/2026,
 * 2026-10-16 …) and in words (16 October 2026, or October 16, 2026 when the
 * chosen order is month-first). The same order and separators are used for
 * every calendar, so a Hebrew or Persian date reads the same way as a
 * Gregorian one.
 *
 * The choice is stored per device (`unveil:date-format`). Changing it fires
 * `unveil:date-format` on window so open tools can re-render.
 */

import { safeGetItem, safeSetItem } from './storage.js';

const STORAGE_KEY = 'unveil:date-format';
export const DATE_FORMAT_EVENT = 'unveil:date-format';

/** order: the sequence of year (y), month (m) and day (d); pad: two-digit day and month. */
export const DATE_FORMATS = [
  { id: 'iso', label: 'YYYY-MM-DD', order: 'ymd', sep: '-', pad: true },
  { id: 'dmy-dot', label: 'DD.MM.YYYY', order: 'dmy', sep: '.', pad: true },
  { id: 'dmy-slash', label: 'DD/MM/YYYY', order: 'dmy', sep: '/', pad: true },
  { id: 'dmy-plain', label: 'D/M/YYYY', order: 'dmy', sep: '/', pad: false },
  { id: 'mdy-slash', label: 'MM/DD/YYYY', order: 'mdy', sep: '/', pad: true },
  { id: 'mdy-plain', label: 'M/D/YYYY', order: 'mdy', sep: '/', pad: false },
  { id: 'ymd-slash', label: 'YYYY/MM/DD', order: 'ymd', sep: '/', pad: true },
];

const BY_ID = Object.fromEntries(DATE_FORMATS.map((f) => [f.id, f]));

/** A sensible default for a locale such as "sv-SE", "en-US", "de-DE". */
export function defaultFormatFor(locale = '') {
  const lang = locale.toLowerCase();
  if (lang === 'en-us' || lang.startsWith('en-ph')) return 'mdy-plain';
  if (/^(de|ru|pl|fi|nb|nn|no|da|cs|sk|tr|uk|ro|hu|et|lv|bg|sr|hr|sl)\b/.test(lang)) return 'dmy-dot';
  if (/^(en-gb|en-au|en-nz|en-ie|en-in|en-za|fr|es|it|pt|el|nl|id|vi)\b/.test(lang)) return 'dmy-slash';
  if (/^(ja|zh|ko)\b/.test(lang)) return 'ymd-slash';
  return 'iso'; // Swedish, Lithuanian, Canadian and anything unknown: unambiguous ISO 8601
}

export function getDateFormat() {
  const saved = safeGetItem(STORAGE_KEY, '');
  if (BY_ID[saved]) return BY_ID[saved];
  const locale = typeof navigator !== 'undefined' ? navigator.language : '';
  return BY_ID[defaultFormatFor(locale)];
}

export function setDateFormat(id) {
  if (!BY_ID[id]) return;
  safeSetItem(STORAGE_KEY, id);
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(DATE_FORMAT_EVENT, { detail: { id } }));
}

const pad = (n, on) => (on ? String(n).padStart(2, '0') : String(n));

/**
 * Numbers for a date in the given format.
 * @param {{ year: number|string, month: number|string, day: number }} date
 *   year may be a string (e.g. "R8" for a Japanese era year); month may be a
 *   string such as "L2" for a leap month
 */
export function formatNumericDate(date, format = getDateFormat()) {
  const f = typeof format === 'string' ? BY_ID[format] || getDateFormat() : format;
  const parts = {
    y: typeof date.year === 'number' && date.year < 0 ? `−${Math.abs(date.year)}` : String(date.year),
    m: typeof date.month === 'number' ? pad(date.month, f.pad) : String(date.month),
    d: pad(date.day, f.pad),
  };
  return [...f.order].map((k) => parts[k]).join(f.sep);
}

/**
 * Words for a date: "16 October 2026", or "October 16, 2026" when the chosen
 * order is month-first. `suffix` follows the year (an era such as "AH").
 */
export function formatNamedDate({ year, monthName, day, suffix = '' }, format = getDateFormat()) {
  const f = typeof format === 'string' ? BY_ID[format] || getDateFormat() : format;
  const y = `${typeof year === 'number' && year < 0 ? `${Math.abs(year)} BCE` : year}${suffix ? ` ${suffix}` : ''}`;
  return f.order === 'mdy' ? `${monthName} ${day}, ${y}` : `${day} ${monthName} ${y}`;
}

const GREGORIAN_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** "YYYY-MM-DD" (an <input type="date"> value) in the chosen numeric format. */
export function formatIsoString(iso, format = getDateFormat()) {
  const m = /^(-?\d{1,6})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return iso || '';
  return formatNumericDate({ year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) }, format);
}

/** A Gregorian date in words in the chosen order. */
export function formatGregorianNamed(year, month, day, format = getDateFormat()) {
  return formatNamedDate({ year, monthName: GREGORIAN_MONTHS[month - 1], day }, format);
}

/** A timestamp (ms) as a local calendar date in the chosen numeric format. */
export function formatTimestampDate(ms, format = getDateFormat()) {
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  return formatNumericDate({ year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() }, format);
}

/** "just now", "5m ago", "3h ago", "12d ago", then the date in the chosen numeric format. */
export function timeAgo(ms) {
  if (!Number.isFinite(ms)) return '';
  const minutes = Math.floor((Date.now() - ms) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatTimestampDate(ms);
}
